import { Injectable } from '@angular/core';
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { ApplicationProxyService } from '../shared/services/applicationproxy.service';
import { FlowManagerService } from '../shared/services/flowmanager.service';
import { TransmitService } from '../shared/services/transmit.service';
import { ApplicationActions } from '../actions/application.actions';
import LogRocket from 'logrocket';
import { whichEnv } from '../shared/helpers/whichEnv';
import { readCookie } from '../shared/helpers/cookieUtil';

@Injectable()
export class HandshakeCanActivate implements CanActivate {
  constructor(
    appActions: ApplicationActions,
    appProxy: ApplicationProxyService,
    flowManager: FlowManagerService,
    transmitService: TransmitService,
    router: Router
  ) {
    this.appActions = appActions;
    this.appProxy = appProxy;
    this.flowManager = flowManager;
    this.transmitService = transmitService;
    this.router = router;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (this.flowManager.getHandshakeFinished() === true) {
      return true;
    }

    return new Promise(async (resolve) => {
      // The Hand Shake :)
      const queryParamString = new URLSearchParams(window.location.search);
      const queryParamObj = {};
      for (const p of queryParamString) {
        queryParamObj[p[0]] = p[1];
      }

      const sso = queryParamObj.sso === 'true';
      delete queryParamObj.sso; // If it exists

      const ticketID = queryParamObj.ticketID || null;
      const relayState = `${document.location.origin}${document.location.pathname}`;

      const replayUrl = `${document.location.origin}${
        document.location.pathname
      }?${queryParamString.toString()}`;
      const replayObject = { Verb: 'GET', Url: replayUrl };

      const payload = { replayObject, sso };

      const helloResponse = await this.appProxy.hello();
      if (helloResponse && helloResponse.config) {
        if (!whichEnv.isDevLike) {
          LogRocket.getSessionURL((sessionURL) => {
            if (window.allytm && window.allytm.ddo) {
              window.allytm.ddo.set('LogRocket.sessionURL', sessionURL);
            }
          });
        }

        this.flowManager.config = helloResponse.config;
        this.flowManager.environment = helloResponse.config.environment;

        this.transmitService.setTransmitObj({
          server: helloResponse.config.transmit.url,
          apiKey: helloResponse.config.transmit.apigee.apiKey,
        });
      } else {
        this.flowManager.environment = 'dev';
      }

      if (ticketID) {
        payload.ticketID = ticketID;
        payload.relayState = relayState;
        this.transmitService.setIncomingTicketID(ticketID);
        await this.transmitService.initializeTransmitSession(payload);
      }

      this.appProxy.handshake(payload, (res) => {
        try {
          this.flowManager.completedQuestionnaire = res.result
            ? res.result.completedQuestionnaire
            : false;
          const sendTkaToQuestionnaire =
            this.flowManager.getAppType() === 'tka' &&
            !this.flowManager.completedQuestionnaire;

          const welcomeBack = queryParamObj.welcomeBack || false;
          this.flowManager.setWBRedirect(welcomeBack);

          this.flowManager.completedCipForm = res.result
            ? res.result.completedCipForm
            : false;

          if (res.result !== undefined) {
            this.flowManager.guid = res.result.guid;
            this.flowManager.acmGuid = res.result.acmGuid;
            this.flowManager.isTkCustomer = res.result.tkCustomer;

            // Set sciVantageId as name on LogRocket session if SSO, otherwise just set application GUID
            if (!whichEnv.isDevLike) {
              if (res.result.altId) {
                LogRocket.identify(res.result.acmGuid || '', {
                  name: this.transmitService.userName || '',
                  guid: res.result.acmGuid || '',
                  investId: res.result.altId || '',
                  sessionId: readCookie('TLTSID'),
                  application: 'OLA',
                });
              } else {
                LogRocket.identify(res.result.guid);
              }
            }

            if (res.result.accountTaxType) {
              this.flowManager.accountTaxType = res.result.accountTaxType;
            }

            if (res.result.settings) {
              this.flowManager.setSettings(res.result.settings);
            }

            if (res.result.auth === true) {
              this.flowManager.setEsignOnFile(res.result.hasEsign);
              this.flowManager.setAuthed(true);
              this.flowManager.setHandshakeFinished(true);

              const acmResponse =
                // eslint-disable-next-line max-len
                (res.result.latestAccountAcm &&
                  res.result.latestAccountAcm.result &&
                  res.result.latestAccountAcm.result.AcmResponse.Payload
                    .CustomerSearchResponse.SearchResult[0]) ||
                null;
              const hasSSN = acmResponse && !!acmResponse.SocialSecurityNumber;

              // PREPOP FROM LATEST INVEST ACCOUNT
              if (
                res.result.hasLatestAccount === true &&
                typeof res.result.latestAccount === 'object'
              ) {
                // Loop through all of the prepop data
                Object.keys(res.result.latestAccount).forEach((key) => {
                  // We dont want the base fields
                  if (typeof res.result.latestAccount[key] === 'object') {
                    this.appActions.setData(key, res.result.latestAccount[key]);
                  } else {
                    this.appActions.setData(
                      `Prepop.${key}`,
                      res.result.latestAccount[key]
                    );
                  }
                });
                const ssn = this.appActions.getData(
                  'PersonalInformation.ssn',
                  {}
                );
                if (Object.keys(ssn).length < 1 && acmResponse) {
                  const ssnToSet = hasSSN
                    ? acmResponse.SocialSecurityNumber
                        .GovernmentIssuedIdentifier.identificationNumber
                    : acmResponse.TaxIdentificationNumber
                        .GovernmentIssuedIdentifier.identificationNumber;
                  this.appActions.setData('PersonalInformation.ssn', ssnToSet);
                }
                this.flowManager.prepoppedTKData = true;
              }

              // USER IS AUTHED
              // If Prepop is successful
              // - TKS has existing account send to /welcome-investor
              // - TKA has existing account AND has completed Questionnaire send to /welcome-investor
              // If Deposit user (no existing Invest account)
              // - If TKA and hasn't completed Questionnaire send to /goal
              // - If TKS OR TKA AND has already completed Questionnaire send to /select-account-type
              if (route.url.length === 0) {
                let routerOptions = {};
                // Angular router doesnt have the params at this stage propagated, it is only in the snapshot
                // Do some sanity checking first
                if (
                  typeof route.queryParams === 'object' &&
                  route.queryParams.app !== undefined
                ) {
                  if (route.queryParams.ticketID !== undefined) {
                    routerOptions = {
                      queryParams: {
                        ...route.queryParams,
                        ticketID: null,
                      },
                      queryParamsHandling: 'merge',
                    };
                  } else {
                    routerOptions.queryParams = route.queryParams;
                  }
                }

                if (sendTkaToQuestionnaire) {
                  this.router.navigate(['/cash-allocation'], routerOptions);
                  resolve(false);
                  return;
                } else if (this.flowManager.prepoppedTKData === true) {
                  this.router.navigate(['/select-account-type'], routerOptions);
                  resolve(false);
                  return;
                } else if (this.flowManager.isWBRedirect) {
                  this.router.navigate(['welcome-back'], {
                    queryParams: {
                      ...route.queryParams,
                      welcomeBack: null,
                      ticketID: null,
                    },
                    queryParamsHandling: 'merge',
                  });
                  resolve(false);
                } else {
                  this.router.navigate(['/select-account-type'], routerOptions);
                  resolve(false);
                }
              }

              resolve(true);
              return;
            }

            if (sendTkaToQuestionnaire) {
              this.router.navigate(['/cash-allocation'], {
                queryParams: route.queryParams,
              });
            }

            if (this.flowManager.isWBRedirect && !res.result.auth) {
              this.router.navigate(['welcome-back'], {
                queryParams: { welcomeBack: null },
                queryParamsHandling: 'merge',
              });
            }

            // Grab authn if we are unauthed and we have not already done so
            if (res.result.auth === false && res.result.authn != null) {
              this.insertSsoForm(res.result.authn);
              this.flowManager.canUnload = true;
              document.forms[0].submit();

              resolve(false);
              return;
            }
          }

          this.flowManager.setAuthed(false);
          this.flowManager.setHandshakeFinished(true);
          console.log('Unsuccessful handshake');
          if (sendTkaToQuestionnaire) {
            this.router.navigate(['/cash-allocation'], {
              queryParams: route.queryParams,
            });
            resolve(false);
          }

          resolve(true);
        } catch (error) {
          throw new Error(`Error - Handshake: ${error}`);
        }
      });
    });
  }

  insertSsoForm(html) {
    const fragment = document.createDocumentFragment();
    const temp = document.createElement('div');
    temp.innerHTML = html;
    while (temp.firstChild) {
      fragment.appendChild(temp.firstChild);
    }
    document.body.removeChild(document.body.childNodes[2]);
    document.body.insertBefore(fragment, document.body.childNodes[0]);
  }
}
